import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { stopAllBloodTestPolling, triggerBloodTestReanalysis } from "@/lib/bloodTestPolling";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Droplets, User, List, GitBranch } from "lucide-react";
import BloodTestDetail from "@/components/blood-tests/BloodTestDetail";
import BloodTestTrackEvolution from "@/components/blood-tests/BloodTestTrackEvolution";
import { REPORT_KIND_LABELS } from "@/components/blood-tests/bloodTestLabels";

const PAGE_TABS = [
  { key: "reports", label: "Tous les bilans", icon: List },
  { key: "tracks", label: "Suivis", icon: GitBranch },
];

export default function CoachBloodTests() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState(searchParams.get("studentId") || "");
  const [viewTab, setViewTab] = useState("reports");
  const [reports, setReports] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedTrackId, setSelectedTrackId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tracksLoading, setTracksLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);

  useEffect(() => {
    api.get("/students").then(r => setStudents(r.data || [])).catch(() => {});
    return () => stopAllBloodTestPolling();
  }, []);

  useEffect(() => {
    if (!studentId) {
      setReports([]);
      setTracks([]);
      setSelectedId(null);
      setSelectedTrackId(null);
      return;
    }
    setLoading(true);
    setTracksLoading(true);
    setSelectedId(null);
    setSelectedTrackId(null);

    Promise.all([
      api.get("/blood-tests", { params: { student_id: studentId } }),
      api.get("/blood-tests/tracks", { params: { student_id: studentId } }),
    ])
      .then(([reportsRes, tracksRes]) => {
        const list = reportsRes.data || [];
        setReports(list);
        setTracks(tracksRes.data || []);
        if (list.length > 0) {
          setSelectedId(list[0].report_id);
        }
      })
      .catch(() => toast.error("Erreur de chargement"))
      .finally(() => {
        setLoading(false);
        setTracksLoading(false);
      });
  }, [studentId]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    api.get(`/blood-tests/${selectedId}`)
      .then(r => setDetail(r.data))
      .catch(() => toast.error("Erreur de chargement"))
      .finally(() => setDetailLoading(false));
  }, [selectedId]);

  const handleStudentChange = (id) => {
    setStudentId(id);
    setSelectedId(null);
    setSelectedTrackId(null);
    setViewTab("reports");
    setSearchParams(id ? { studentId: id } : {});
  };

  const handleSelectReport = (reportId) => {
    setSelectedId(reportId);
    setViewTab("reports");
  };

  const handleReanalyze = async () => {
    if (!selectedId) return;
    setReanalyzing(true);
    setDetail(prev => (prev ? { ...prev, ai_status: "processing" } : prev));
    await triggerBloodTestReanalysis(selectedId, {
      onUpdate: (data) => {
        if (!data) return;
        setDetail(data);
        setReports(prev => prev.map(r => (r.report_id === selectedId ? { ...r, ...data } : r)));
      },
    });
    setReanalyzing(false);
  };

  const statusBadge = (status) => {
    const map = {
      done: { label: "Analysé", className: "bg-green-100 text-green-700" },
      processing: { label: "En cours", className: "bg-blue-100 text-blue-700" },
      extracted: { label: "Extrait", className: "bg-indigo-100 text-indigo-700" },
      parsed: { label: "Biomarqueurs OK", className: "bg-violet-100 text-violet-700" },
      pending: { label: "En attente", className: "bg-slate-100 text-slate-600" },
      error: { label: "Erreur", className: "bg-red-100 text-red-700" },
    };
    const cfg = map[status] || map.pending;
    return <Badge className={cfg.className}>{cfg.label}</Badge>;
  };

  const kindBadge = (kind) => {
    const cfg = REPORT_KIND_LABELS[kind] || REPORT_KIND_LABELS.standalone;
    return <Badge className={cfg.className}>{cfg.label}</Badge>;
  };

  return (
    <div data-testid="coach-blood-tests-page">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-red-100 flex items-center justify-center flex-shrink-0">
          <Droplets className="w-6 h-6 text-red-600" />
        </div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">Bilans sanguins</h1>
          <p className="text-slate-400 mt-1 text-sm">Bilans ponctuels et suivis dans le temps avec interprétation IA</p>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-4 items-end">
        <div className="min-w-[220px]">
          <label className="text-xs font-medium text-slate-500 mb-1.5 block">Élève</label>
          <Select value={studentId} onValueChange={handleStudentChange}>
            <SelectTrigger><SelectValue placeholder="Sélectionner un élève" /></SelectTrigger>
            <SelectContent>
              {students.map(s => (
                <SelectItem key={s.user_id} value={s.user_id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!studentId ? (
        <div className="text-center py-16 mt-8">
          <User className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Sélectionnez un élève pour voir ses bilans sanguins</p>
        </div>
      ) : (
        <>
          <div className="mt-6 flex gap-2 border-b border-slate-100">
            {PAGE_TABS.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setViewTab(tab.key)}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    viewTab === tab.key
                      ? "border-red-500 text-red-600"
                      : "border-transparent text-slate-500 hover:text-slate-700"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div className="mt-8 grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1 space-y-3">
              {viewTab === "reports" ? (
                loading ? (
                  <p className="text-slate-400 text-sm">Chargement…</p>
                ) : reports.length === 0 ? (
                  <Card className="border-dashed">
                    <CardContent className="p-8 text-center">
                      <Droplets className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500 text-sm">Cet élève n'a pas encore uploadé de bilan</p>
                    </CardContent>
                  </Card>
                ) : (
                  reports.map(r => (
                    <button
                      key={r.report_id}
                      onClick={() => setSelectedId(r.report_id)}
                      className={`w-full text-left rounded-xl border p-4 transition-colors ${
                        selectedId === r.report_id
                          ? "border-red-200 bg-red-50/50"
                          : "border-slate-100 bg-white hover:border-slate-200"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="font-medium text-slate-900 truncate">{r.title}</span>
                        <div className="flex gap-1.5 shrink-0">
                          {kindBadge(r.report_kind || "standalone")}
                          {statusBadge(r.ai_status)}
                        </div>
                      </div>
                      <div className="flex gap-3 mt-1 text-xs text-slate-500 flex-wrap">
                        {r.lab_date && <span>{new Date(r.lab_date + "T12:00:00").toLocaleDateString("fr-FR")}</span>}
                        {r.marker_count > 0 && <span>{r.marker_count} marqueurs</span>}
                        {r.track_name && <span className="text-indigo-600">↳ {r.track_name}</span>}
                      </div>
                    </button>
                  ))
                )
              ) : tracksLoading ? (
                <p className="text-slate-400 text-sm">Chargement…</p>
              ) : tracks.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="p-8 text-center">
                    <GitBranch className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 text-sm">Aucun suivi pour cet élève</p>
                  </CardContent>
                </Card>
              ) : (
                tracks.map(t => (
                  <button
                    key={t.track_id}
                    onClick={() => { setSelectedTrackId(t.track_id); setSelectedId(null); }}
                    className={`w-full text-left rounded-xl border p-4 transition-colors ${
                      selectedTrackId === t.track_id
                        ? "border-red-200 bg-red-50/50"
                        : "border-slate-100 bg-white hover:border-slate-200"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-slate-900 truncate">{t.name}</span>
                      <Badge className="bg-red-100 text-red-700">{t.report_count} bilan{t.report_count > 1 ? "s" : ""}</Badge>
                    </div>
                    {t.last_lab_date && (
                      <p className="text-xs text-slate-500 mt-1">
                        Dernier : {new Date(t.last_lab_date + "T12:00:00").toLocaleDateString("fr-FR")}
                      </p>
                    )}
                  </button>
                ))
              )}
            </div>

            <div className="lg:col-span-2">
              {viewTab === "tracks" && selectedTrackId ? (
                <BloodTestTrackEvolution
                  trackId={selectedTrackId}
                  studentId={studentId}
                  onSelectReport={handleSelectReport}
                />
              ) : viewTab === "tracks" && !selectedTrackId ? (
                <div className="text-center py-16 text-slate-400">
                  <GitBranch className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Sélectionnez un suivi pour voir l'évolution</p>
                </div>
              ) : !selectedId ? (
                <div className="text-center py-16 text-slate-400">
                  <p>Sélectionnez un bilan</p>
                </div>
              ) : (
                <BloodTestDetail
                  report={detail}
                  loading={detailLoading}
                  onReanalyze={handleReanalyze}
                  reanalyzing={reanalyzing}
                />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}